import React, { useState, useEffect, useRef, useCallback } from 'react';
import './PluginPresentationJava.scss';

interface FileTab {
    id: string;
    label: string;
    language: string;
    code: string;
    typingSpeed: number;
    pauseAfter: number;
}

interface Tab {
    id: string;
    label: string;
    language: string;
    fullCode: string;
    lines: string[];
    charCount: number;
    currentLineIndex: number;
    isComplete: boolean;
    typingSpeed: number;
    pauseAfter: number;
    _typed: string;
    _initialLines: string[];
}

const PluginPresentationJava: React.FC = () => {
    const FILES: FileTab[] = [
        {
            id: 'java',
            label: 'Main.java',
            language: 'java',
            code: `package com.example.plugin;

import org.bukkit.plugin.java.JavaPlugin;
import org.bukkit.command.Command;
import org.bukkit.command.CommandSender;
import org.bukkit.entity.Player;

public class Main extends JavaPlugin {

    @Override
    public void onEnable() {
        getLogger().info("§aPlugin enabled!");
    }

    @Override
    public void onDisable() {
        getLogger().info("§cPlugin disabled!");
    }

    @Override
    public boolean onCommand(CommandSender sender, 
                             Command command, 
                             String label, String[] args) {
        if (command.getName().equalsIgnoreCase("hello")) {
            if (sender instanceof Player) {
                Player player = (Player) sender;
                player.sendMessage("§aHello, " + player.getName() + "!");
            } else {
                sender.sendMessage("§aHello, console!");
            }
            return true;
        }
        return false;
    }
}`,
            typingSpeed: 40,
            pauseAfter: 1200
        },
        {
            id: 'yaml',
            label: 'plugin.yml',
            language: 'yaml',
            code: `name: ExamplePlugin
version: 1.0.0
main: com.example.plugin.Main
api-version: 1.20
commands:
    hello:
        description: Say hello to a player or console
        usage: /<command>
        permission: example.hello
permissions:
    example.hello:
        description: Allows using /hello
        default: true`,
            typingSpeed: 50,
            pauseAfter: 800
        }
    ];

    const TERMINAL_COMMAND = 'javac -cp paper.jar -d . Main.java';
    const TERMINAL_OUTPUT = `Compiling 1 source file to .

──────────────────────────────────────────────
✅ Compilation successful!
──────────────────────────────────────────────

ℹ️  Run with: java -cp paper.jar:target/classes com.example.plugin.Main
📦 Plugin built in ./com/example/plugin/Main.class

⚠️  Paper 1.20 API used – ensure server version matches.`;

    const [tabs, setTabs] = useState<Record<string, Tab>>({});
    const [activeTabId, setActiveTabId] = useState<string | null>(null);
    const [isRunning, setIsRunning] = useState(false);
    const [stopRequested, setStopRequested] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [isPausing, setIsPausing] = useState(false);
    const [terminalOpened, setTerminalOpened] = useState(false);
    const [terminalLines, setTerminalLines] = useState<string[]>([]);
    const [statusDot, setStatusDot] = useState<'idle' | 'typing' | 'compiling' | 'done'>('idle');
    const [statusText, setStatusText] = useState('Pronto');
    const [statusLang, setStatusLang] = useState('Java');
    const [statusLine, setStatusLine] = useState('Linea 1');
    const [statusChars, setStatusChars] = useState('0 caratteri');

    const codeAreaRef = useRef<HTMLDivElement>(null);
    const lineNumbersRef = useRef<HTMLDivElement>(null);
    const terminalContentRef = useRef<HTMLDivElement>(null);
    const isRunningRef = useRef(false);
    const stopRequestedRef = useRef(false);

    const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    // ---------- HIGHLIGHTING ----------
    const highlightJava = (line: string): React.ReactNode => {
        const parts: React.ReactNode[] = [];
        let lastIndex = 0;
        const regex = /("(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|\/\/.*|\/\*[\s\S]*?\*\/|\b(?:abstract|assert|boolean|break|byte|case|catch|char|class|const|continue|do|double|else|enum|extends|final|finally|float|for|goto|if|implements|import|instanceof|int|interface|long|native|new|package|private|protected|public|return|short|static|strictfp|super|switch|synchronized|this|throw|throws|transient|try|void|volatile|while)\b|@\w+|\b\d+\.?\d*(?:px|em|rem|%|vh|vw|s|ms)?\b|[{}[\]();,.:&])/g;
        let match;

        while ((match = regex.exec(line)) !== null) {
            if (match.index > lastIndex) {
                parts.push(line.substring(lastIndex, match.index));
            }

            const token = match[0];
            let className = '';

            if (token.match(/^["']/)) className = 'syn-str';
            else if (token.match(/^\/\//) || token.match(/^\/\*/)) className = 'syn-comment';
            else if (token.match(/^@/)) className = 'syn-annotation';
            else if (token.match(/^(abstract|assert|boolean|break|byte|case|catch|char|class|const|continue|do|double|else|enum|extends|final|finally|float|for|goto|if|implements|import|instanceof|int|interface|long|native|new|package|private|protected|public|return|short|static|strictfp|super|switch|synchronized|this|throw|throws|transient|try|void|volatile|while)\b/)) className = 'syn-keyword';
            else if (token.match(/^\d/)) className = 'syn-num';
            else if (token === '{' || token === '}') className = 'syn-brace';
            else if (token === '.' || token === ':' || token === '&') className = 'syn-keyword';
            else if (token.match(/^[A-Z]/)) className = 'syn-type'; // classi
            else className = 'syn-var';

            parts.push(
                className ? <span key={lastIndex} className={className}>{token}</span> : token
            );
            lastIndex = regex.lastIndex;
        }

        if (lastIndex < line.length) {
            parts.push(line.substring(lastIndex));
        }

        return <>{parts}</>;
    };

    const highlightYAML = (line: string): React.ReactNode => {
        const parts: React.ReactNode[] = [];
        let lastIndex = 0;
        const regex = /("(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|#.*|\b(?:true|false|null)\b|\b\d+\.?\d*\b|[\w-]+(?=:)|:)/g;
        let match;

        while ((match = regex.exec(line)) !== null) {
            if (match.index > lastIndex) {
                parts.push(line.substring(lastIndex, match.index));
            }

            const token = match[0];
            let className = '';

            if (token.match(/^["']/)) className = 'syn-str';
            else if (token.match(/^#/)) className = 'syn-comment';
            else if (token.match(/^(true|false|null)\b/)) className = 'syn-num';
            else if (token.match(/^\d/)) className = 'syn-num';
            else if (token.match(/^[\w-]+(?=:)/)) className = 'syn-property'; // chiave YAML
            else if (token === ':') className = 'syn-keyword';
            else className = 'syn-var';

            parts.push(
                className ? <span key={lastIndex} className={className}>{token}</span> : token
            );
            lastIndex = regex.lastIndex;
        }

        if (lastIndex < line.length) {
            parts.push(line.substring(lastIndex));
        }

        return <>{parts}</>;
    };

    const highlightLine = (line: string, language: string): React.ReactNode => {
        if (language === 'java') {
            return highlightJava(line);
        } else if (language === 'yaml') {
            return highlightYAML(line);
        }
        return <>{line}</>;
    };

    // ---------- INIT ----------
    const initTabs = useCallback(() => {
        const newTabs: Record<string, Tab> = {};
        FILES.forEach((file) => {
            const lines = file.code.split('\n');
            newTabs[file.id] = {
                id: file.id,
                label: file.label,
                language: file.language,
                fullCode: file.code,
                lines: lines.length > 0 ? lines : [''],
                charCount: 0,
                currentLineIndex: 0,
                isComplete: false,
                typingSpeed: file.typingSpeed || 40,
                pauseAfter: file.pauseAfter || 800,
                _typed: '',
                _initialLines: lines,
            };
        });
        const firstId = FILES[0].id;
        setActiveTabId(firstId);
        setTabs(newTabs);
        setStatusDot('idle');
        setStatusText('Pronto');
    }, []);

    const updateTab = (tabId: string, updates: Partial<Tab>) => {
        setTabs(prev => ({
            ...prev,
            [tabId]: { ...prev[tabId], ...updates }
        }));
    };

    // ---------- TYPING ----------
    const typeFile = useCallback(async (fileId: string, fileLabel: string, fullText: string, typingSpeed: number, pauseAfter: number, lang: string) => {
        const initialLines = fullText.split('\n');
        updateTab(fileId, {
            lines: initialLines.length > 0 ? initialLines : [''],
            charCount: 0,
            currentLineIndex: 0,
            isComplete: false,
            _typed: ''
        });

        setIsTyping(true);
        setIsPausing(false);
        setActiveTabId(fileId);
        setStatusDot('typing');
        setStatusText(`Scrivendo ${fileLabel}…`);
        setStatusLang(lang.charAt(0).toUpperCase() + lang.slice(1));

        let typed = '';
        for (let i = 0; i < fullText.length; i++) {
            if (stopRequestedRef.current) {
                setIsTyping(false);
                return;
            }

            const ch = fullText[i];
            typed += ch;
            const newLines = typed.split('\n');
            const currentLineIndex = newLines.length - 1;

            updateTab(fileId, {
                _typed: typed,
                lines: newLines,
                charCount: typed.length,
                currentLineIndex: currentLineIndex
            });

            setStatusLine(`Linea ${currentLineIndex + 1}`);
            setStatusChars(`${typed.length} caratteri`);

            if (codeAreaRef.current) {
                codeAreaRef.current.scrollTop = codeAreaRef.current.scrollHeight;
            }

            await sleep(typingSpeed);
        }

        updateTab(fileId, { isComplete: true });
        setIsTyping(false);
        setStatusDot('done');
        setStatusText(`${fileLabel} completato`);

        setIsPausing(true);
        await sleep(pauseAfter);
        setIsPausing(false);
    }, []);

    // ---------- TERMINAL ----------
    const typeTerminalOutput = useCallback(async () => {
        setTerminalOpened(true);
        setStatusDot('compiling');
        setStatusText('Compilazione in corso…');
        setTerminalLines([`$ ${TERMINAL_COMMAND}`]);

        await sleep(800);

        const outputLines = TERMINAL_OUTPUT.split('\n');
        const allLines = [`$ ${TERMINAL_COMMAND}`];

        for (const line of outputLines) {
            allLines.push(line);
            setTerminalLines([...allLines]);
            await sleep(100);
            if (terminalContentRef.current) {
                terminalContentRef.current.scrollTop = terminalContentRef.current.scrollHeight;
            }
        }

        await sleep(400);
        setStatusDot('done');
        setStatusText('✅ Compilazione riuscita');
    }, []);

    // ---------- START / RESTART ----------
    const startPresentation = useCallback(async () => {
        if (isRunningRef.current) return;
        isRunningRef.current = true;
        stopRequestedRef.current = false;
        setIsRunning(true);
        setStopRequested(false);
        setTerminalOpened(false);
        setTerminalLines([]);

        initTabs();
        await sleep(300);

        for (let i = 0; i < FILES.length; i++) {
            if (stopRequestedRef.current) break;
            const file = FILES[i];
            if (i > 0) {
                setActiveTabId(file.id);
                await sleep(300);
            }
            await typeFile(file.id, file.label, file.code, file.typingSpeed, file.pauseAfter, file.language);
            if (stopRequestedRef.current) break;
        }

        if (!stopRequestedRef.current) {
            await sleep(400);
            await typeTerminalOutput();
        }

        isRunningRef.current = false;
        setIsRunning(false);
    }, [initTabs, typeFile, typeTerminalOutput, FILES]);

    const restartPresentation = useCallback(() => {
        stopRequestedRef.current = true;
        setTimeout(() => {
            setTerminalOpened(false);
            setTerminalLines([]);
            startPresentation();
        }, 300);
    }, [startPresentation]);

    useEffect(() => {
        const timer = setTimeout(() => {
            startPresentation();
        }, 600);
        return () => clearTimeout(timer);
    }, [startPresentation]);

    const currentTab = activeTabId ? tabs[activeTabId] : null;

    return (
        <div className="idea-window">
            {/* TITLE BAR */}
            <div className="title-bar" onClick={restartPresentation}>
                <div className="title-bar-left">
                    <div className="window-controls">
                        <span className="win-close"></span>
                        <span className="win-minimize"></span>
                        <span className="win-maximize"></span>
                    </div>
                    <div className="title-text">
                        <span>IntelliJ IDEA</span>
                    </div>
                </div>
                <div className="title-bar-right">
                    <span>Plugin & Mod</span>
                </div>
            </div>

            {/* TAB BAR */}
            <div className="tab-bar">
                {Object.values(tabs).map(tab => (
                    <div
                        key={tab.id}
                        className={`tab ${activeTabId === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveTabId(tab.id)}
                    >
                        <span>{tab.label}</span>
                    </div>
                ))}
            </div>

            {/* EDITOR */}
            <div className="editor-container">
                <div className="line-numbers" ref={lineNumbersRef}>
                    {currentTab?.lines.map((_, i) => (
                        <div key={i} className="ln">
                            {i + 1}
                        </div>
                    ))}
                </div>
                <div className="code-area" ref={codeAreaRef}>
                    <div className="code-lines">
                        {currentTab?.lines.map((line, idx) => (
                            <div key={idx} className="code-line">
                                {highlightLine(line, currentTab.language)}
                                {idx === currentTab.currentLineIndex && (isTyping || isPausing) && <span className="cursor-blink"></span>}
                            </div>
                        ))}
                    </div>
                    {currentTab && (
                        <div className="lang-badge-wrapper">
                            <div className={`lang-badge ${currentTab.language}`}>
                                {currentTab.language === 'java' ? 'JAVA' : 'YAML'}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* TERMINAL */}
            {terminalOpened && (
                <div className="terminal-panel open">
                    <div className="terminal-header">
                        <span className="terminal-title">Terminal</span>
                        <span className="terminal-close">×</span>
                    </div>
                    <div className="terminal-content" ref={terminalContentRef}>
                        {terminalLines.map((line, idx) => (
                            <div key={idx} className={`terminal-line ${line.startsWith('$') ? 'terminal-prompt' : ''}`}>
                                {line}
                            </div>
                        ))}
                        {statusDot === 'compiling' && <span className="terminal-cursor"></span>}
                    </div>
                </div>
            )}

            {/* STATUS BAR */}
            <div className="status-bar" onClick={restartPresentation}>
                <div className="status-left">
                    <span className={`status-dot ${statusDot}`}></span>
                    <span>{statusText}</span>
                    <span>{statusLang}</span>
                    <span>{statusLine}</span>
                </div>
                <div className="status-right">
                    <span>UTF-8</span>
                    <span>LF</span>
                    <span>{statusChars}</span>
                </div>
            </div>
        </div>
    );
};

export default PluginPresentationJava;